import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircleCheck, Info, Loader, Plug, TriangleAlert, X } from "lucide-react";

/**
 * Add or edit one account.
 *
 * THE ORDER OF CHECKS IS THE SPECIFICATION, and it is enforced here rather than
 * left to whoever clicks in what order:
 *
 *   1  username must be chosen. Nothing else runs until it is, because the IP
 *      rule is "same broker, DIFFERENT user" and without a user there is no
 *      question to ask.
 *   2  Test connection. The broker names itself, and that name is what the IP
 *      check is scoped to.
 *   3  the bot-IP check, against that real broker.
 *   4  Save, only once username and broker are present, the test passed, and any
 *      warning has been answered.
 *
 * WHY THE IP CHECK IS SOMETIMES DEFERRED
 * The check fires when you leave the IP field — but it is scoped to a broker, and
 * on a brand-new account the broker is not known until the test has run. So if
 * the broker is already known it runs immediately, and if it is not the check is
 * HELD and runs the moment the test completes. It never runs against an unknown
 * broker: a check scoped to the wrong broker would answer confidently and wrongly,
 * which is worse than not answering.
 *
 * Changing the host invalidates both, because a different host can be a different
 * broker.
 *
 * HISTORICAL MODE (`mode="old"`) differs in what is REQUIRED, not in what is
 * offered. A retired account may have no credentials left, so the password and
 * the test are optional and the broker can be typed — it saves incomplete on
 * purpose, because it is going LIVE that is gated, not recording.
 *
 * But the password and the Test button are still THERE. An old account you do
 * still have the credentials for can be completed in one pass, and more to the
 * point: an old account brought back into the pair needs a password, a passing
 * test and a bot IP, so hiding those fields here would have meant adding it,
 * saving, reopening it and filling them in a second form. If the test succeeds it
 * names the broker and that name wins over the typed one — the server's own name
 * for itself is better evidence than a spelling.
 *
 * The password is write-only. The API never returns it, so the field is always
 * blank; leaving it blank on an edit keeps whatever is stored rather than
 * clearing it, or an unrelated edit would wipe a credential.
 */

const BLANK = {
  brokerId: "",
  userId: "",
  login: "",
  host: "",
  port: "443",
  platform: "MT5",
  password: "",
  botIp: "",
  botServerName: "",
  leverage: "",
  note: "",
};

/**
 * `container` — WHICH CONTAINER A NEW ACCOUNT IS CREATED IN.
 *
 * An account belongs to one container, and the create route REFUSES a request
 * that does not name one rather than picking the default: an account appearing
 * in a book nobody chose is worse than a refusal. So this is required for a
 * create, and irrelevant for an edit — the account already knows where it
 * lives, and moving it between containers is not something this form does.
 */
/**
 * `servers` — EVERY BOT MACHINE ALREADY KNOWN, for the two linked dropdowns.
 *
 * A machine is one record with an IP and a label (models/BotServerSchema.js:
 * unique on the IP, one label per IP). Because the two facts live together,
 * choosing a machine is ONE decision and gets one dropdown showing both - there
 * is nothing to keep in step, which is a stronger guarantee than keeping two
 * controls synchronised.
 *
 * Typing them freely is what let "VPS-1" and "vps 1" become two names for one
 * machine, which is the exact thing the bot-IP duplicate check exists to catch.
 * The two fields reappear only in the create form below, where the machine does
 * not exist yet and the pairing is being made rather than chosen.
 */
export default function AccountForm({ mode, account, brokers, users, servers = [], token, container, onClose, onSaved }) {
  const isOld = mode === "old";
  const isEdit = mode === "edit";

  const [f, setF] = useState(() =>
    isEdit && account
      ? {
          brokerId: account.brokerId || "",
          userId: account.userId || "",
          login: String(account.login || ""),
          host: account.host || "",
          port: String(account.port || 443),
          platform: account.platform || "MT5",
          password: "",
          botIp: account.botIp || "",
          botServerName: account.botServerName || "",
          leverage: account.leverage || "",
          note: account.note || "",
        }
      : { ...BLANK }
  );

  const [test, setTest] = useState(() =>
    isEdit && account && account.lastTestOk
      ? { status: "ok", company: account.lastTestBroker || "", error: "", warning: "", stale: true }
      : { status: "idle", company: "", error: "", warning: "", stale: false }
  );
  const [ip, setIp] = useState(null); // the ip-check result
  const [ipPending, setIpPending] = useState(false); // held until the broker is known
  const [ipChecking, setIpChecking] = useState(false);
  const [overrideIp, setOverrideIp] = useState(false); // "anyway continue with that IP"
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newUser, setNewUser] = useState("");
  const [newBroker, setNewBroker] = useState("");
  /* Adding a machine, rather than choosing one. Open by default when there are
     none to choose from, so a first-run form is not a dropdown with nothing in
     it and no visible way forward. */
  const [addingServer, setAddingServer] = useState(servers.length === 0);
  const [newIp, setNewIp] = useState("");
  const [newIpName, setNewIpName] = useState("");

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const jsonHeaders = useMemo(() => ({ ...auth, "Content-Type": "application/json" }), [auth]);

  const post = useCallback(
    async (url, body, method = "POST") => {
      const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.message || data.error || `failed (${res.status})`), { data });
      return data;
    },
    [jsonHeaders]
  );

  /* Changing the host throws away the test AND the IP result: a different host
     can be a different broker, and both answers were scoped to the old one. */
  const onHostChange = (e) => {
    set("host")(e);
    setTest({ status: "idle", company: "", error: "", warning: "", stale: false });
    setIp(null);
    setOverrideIp(false);
  };

  /* A new password means the proven credential is no longer the one on the form. */
  const onPasswordChange = (e) => {
    set("password")(e);
    if (test.status === "ok") setTest((t) => ({ ...t, status: "idle", stale: false }));
  };

  /**
   * `ipArg` LETS A CALLER NAME THE IP IT JUST CHOSE.
   *
   * `f.botIp` here is the value captured when this callback was built, so a
   * dropdown that sets the IP and checks it in the same handler would check the
   * PREVIOUS one - and the answer would look authoritative. Callers that already
   * know the new value pass it; the blur handler and the post-test effect still
   * rely on state, which by then has settled.
   */
  const runIpCheck = useCallback(
    async (brokerId, ipArg) => {
      const theIp = String(ipArg == null ? f.botIp : ipArg).trim();
      if (!theIp) {
        setIp(null);
        return;
      }
      setIpChecking(true);
      try {
        const result = await post("/api/dev/accounts/ip-check", {
          ip: theIp,
          brokerId: brokerId || f.brokerId,
          userId: f.userId,
          accountId: isEdit && account ? account.id : "",
        });
        setIp(result);
        setOverrideIp(false);
      } catch (err) {
        setError(err.message);
      } finally {
        setIpChecking(false);
        setIpPending(false);
      }
    },
    [f.botIp, f.brokerId, f.userId, isEdit, account, post]
  );

  /* The blur handler that used to live here is gone with the free-text field.
     A dropdown commits on CHANGE, so `pickServer` and `addServer` run the check
     themselves and pass the IP they just chose - waiting for a blur that a
     <select> never fires would have left the machine unchecked. */

  const runTest = async () => {
    setError("");
    setTest({ status: "testing", company: "", error: "", warning: "", stale: false });
    try {
      const body = isEdit && account && !f.password
        ? { accountId: account.id, host: f.host, port: Number(f.port), login: Number(f.login) }
        : { host: f.host, port: Number(f.port), login: Number(f.login), password: f.password };
      if (isEdit && account) body.accountId = account.id;

      const r = await post("/api/dev/accounts/test", body);
      if (!r.ok) {
        setTest({ status: "fail", company: "", error: r.error, warning: "", stale: false });
        return;
      }

      /* The broker named itself. Match it to a broker record case-insensitively,
         and create one if it is new — this is the "save it automatically" part:
         the name came from the server, so it is not a guess worth confirming. */
      let brokerId = "";
      const found = brokers.find((b) => b.name.toLowerCase() === (r.company || "").toLowerCase());
      if (found) brokerId = found.id;
      else if (r.company) {
        try {
          const created = await post("/api/dev/accounts/brokers", { name: r.company });
          brokerId = created.broker.id;
        } catch (err) {
          /* 409 means it exists after all — a race, or a name that differs only
             by case. Use the one the server points at. */
          if (err.data && err.data.broker) brokerId = err.data.broker.id;
          else throw err;
        }
      }

      setF((prev) => ({ ...prev, brokerId: brokerId || prev.brokerId, currency: r.currency }));
      setTest({
        status: "ok",
        company: r.company || "",
        error: "",
        warning: r.warning || "",
        stale: false,
      });

      /* Refresh the page BEHIND this form. The test route records the result on
         the account, so the card's "missing: tested connection" is already out of
         date the moment this returns — and without this it stayed wrong until the
         next reload, which made it look as though testing had done nothing. The
         confusing symptom was that updating a SECOND account was what finally
         made the FIRST one turn ready. */
      onSaved({ silent: true });

      /* The held IP check can now run, against the broker that just named
         itself. */
      if (f.botIp.trim() && f.userId) runIpCheck(brokerId || f.brokerId);
    } catch (err) {
      setTest({ status: "fail", company: "", error: err.message, warning: "", stale: false });
    }
  };

  const addUser = async () => {
    const name = newUser.trim();
    if (!name) return;
    setError("");
    try {
      const r = await post("/api/dev/accounts/users", { name });
      setF((prev) => ({ ...prev, userId: r.user.id }));
      setNewUser("");
      onSaved({ silent: true }); // refresh the lists behind the form
    } catch (err) {
      /* An existing name is not a failure here — it is the record we wanted. */
      if (err.data && err.data.user) {
        setF((prev) => ({ ...prev, userId: err.data.user.id }));
        setNewUser("");
        setError(`Using the existing user "${err.data.user.name}".`);
        onSaved({ silent: true });
      } else setError(err.message);
    }
  };

  /**
   * ONE CHOICE, BOTH FIELDS.
   *
   * The IP and the label are written together from the chosen record, so they
   * cannot disagree, and the ip-check is re-run for the new machine because its
   * whole answer ("who else is on this IP at this broker") changes with it.
   * Leaving the previous verdict on screen under a different IP is the one
   * outcome worse than showing none.
   */
  const pickServer = (serverId) => {
    const chosen = servers.find((x) => String(x.id) === String(serverId));
    setIp(null);
    setOverrideIp(false);
    if (!chosen) {
      setF((prev) => ({ ...prev, botIp: "", botServerName: "" }));
      return;
    }
    setF((prev) => ({ ...prev, botIp: chosen.ip, botServerName: chosen.name || "" }));
    if (f.userId && f.brokerId) runIpCheck(f.brokerId, chosen.ip);
    else if (f.userId) setIpPending(true);
  };

  /**
   * Create a machine, then select it — mirroring addUser and addBroker above.
   *
   * Saving the account would upsert the machine anyway (see the botIp branch of
   * applyAccountFields), so this exists for what that cannot give: the record
   * appears in both dropdowns for the NEXT account immediately, and the ip-check
   * runs now rather than after a save.
   */
  const addServer = async () => {
    const ipText = newIp.trim();
    if (!ipText) return;
    setError("");
    try {
      const r = await post("/api/dev/accounts/servers", { ip: ipText, name: newIpName.trim() });
      setF((prev) => ({ ...prev, botIp: r.server.ip, botServerName: r.server.name || "" }));
      setNewIp("");
      setNewIpName("");
      setAddingServer(false);
      setIp(null);
      setOverrideIp(false);
      onSaved({ silent: true }); // refresh the lists behind the form
      if (f.userId && f.brokerId) runIpCheck(f.brokerId, r.server.ip);
      else if (f.userId) setIpPending(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const addBroker = async () => {
    const name = newBroker.trim();
    if (!name) return;
    setError("");
    try {
      const r = await post("/api/dev/accounts/brokers", { name });
      setF((prev) => ({ ...prev, brokerId: r.broker.id }));
      setNewBroker("");
      onSaved({ silent: true });
    } catch (err) {
      if (err.data && err.data.broker) {
        setF((prev) => ({ ...prev, brokerId: err.data.broker.id }));
        setNewBroker("");
        setError(`Using the existing broker "${err.data.broker.name}".`);
        onSaved({ silent: true });
      } else setError(err.message);
    }
  };

  /* What still stops a save. Listed rather than just disabling the button, so it
     is never a mystery why nothing happens when you click. */
  const blockers = [];
  if (!f.userId) blockers.push("choose a username");
  if (!f.brokerId) blockers.push(isOld ? "choose a broker" : "run the connection test to get the broker");
  if (!String(f.login).trim()) blockers.push("login");
  if (!f.host.trim()) blockers.push("host");
  if (!isOld) {
    const credentialsChanged = !isEdit || Boolean(f.password);
    if (credentialsChanged && test.status !== "ok") blockers.push("a successful connection test");
    if (!isEdit && !f.password) blockers.push("investor password");
  }
  if (ip && ip.level === "warning" && !overrideIp) blockers.push("answer the bot-IP warning");

  /* WHICH server is selected, derived from the IP rather than stored. The IP is
     what the account actually carries and what gets saved, so deriving keeps a
     single source of truth: there is no second piece of state to fall out of
     step, and an account whose IP is not in `servers` resolves to "" and is
     handled explicitly below. */
  const selectedServerId = (servers.find((sv) => sv.ip === f.botIp.trim()) || {}).id || "";

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        brokerId: f.brokerId,
        userId: f.userId,
        login: Number(f.login),
        host: f.host.trim(),
        port: Number(f.port) || 443,
        platform: f.platform.trim() || "MT5",
        botIp: f.botIp.trim(),
        botServerName: f.botServerName.trim(),
        leverage: f.leverage.trim(),
        note: f.note.trim(),
      };
      if (f.password) body.password = f.password;

      if (isEdit && account) await post(`/api/dev/accounts/${account.id}`, body, "PUT");
      else {
        /* The container is on the URL, not in the body: the route reads it from
           the query and re-asserts it after mapping the fields, so a body
           carrying its own containerId cannot redirect the write. */
        await post(`/api/dev/accounts?container=${encodeURIComponent(container || "")}`, body);
      }

      onSaved({});
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* Escape closes, which matters for a dialog covering the page. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const title = isEdit ? `Edit ${account.login}` : isOld ? "Add an old account" : "Add an account";

  return (
    <div className="dev-modal-back" role="dialog" aria-modal="true" aria-label={title}>
      <div className="dev-modal">
        <div className="dev-modal-head">
          <h3>{title}</h3>
          <button type="button" className="dev-icon-btn" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="dev-modal-body">
          {isOld && (
            <p className="dev-hint">
              For an account that is no longer in the pair. Nothing here is required beyond a user,
              a broker, a login and a host — it links to any stored history by login and saves
              incomplete. If you still have the investor password, fill it in and test now: that is
              what an old account needs before it can be put back into a live pair, and doing it here
              saves reopening this account later.
            </p>
          )}

          {error && (
            <div className="dev-error" role="alert">
              <TriangleAlert size={15} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {/* ---------------------------- 1. the user --------------------------- */}
          <div className="dev-field">
            <label htmlFor="af-user">
              Username <span className="dev-step">step 1</span>
            </label>
            <select id="af-user" value={f.userId} onChange={set("userId")}>
              <option value="">— choose —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <div className="dev-inline-add">
              <input
                type="text"
                value={newUser}
                onChange={(e) => setNewUser(e.target.value)}
                placeholder="or add a new person"
              />
              <button type="button" className="dev-btn dev-btn-sm" onClick={addUser} disabled={!newUser.trim()}>
                Add
              </button>
            </div>
            <p className="dev-help">
              One record per person, spanning brokers. The bot-IP rule compares users, so nothing
              else can be judged until this is set.
            </p>
          </div>

          {/* ------------------------- login / host / port --------------------- */}
          <div className="dev-row">
            <div className="dev-field">
              <label htmlFor="af-login">Login</label>
              <input id="af-login" type="text" inputMode="numeric" value={f.login} onChange={set("login")} />
            </div>
            <div className="dev-field">
              <label htmlFor="af-host">Host</label>
              <input id="af-host" type="text" value={f.host} onChange={onHostChange} placeholder="31.14.254.145" />
            </div>
            <div className="dev-field dev-field-narrow">
              <label htmlFor="af-port">Port</label>
              <input id="af-port" type="text" inputMode="numeric" value={f.port} onChange={set("port")} />
            </div>
          </div>

          {/* ---------------------------- the password -------------------------- */}
          <div className="dev-field">
            <label htmlFor="af-pw">
                Investor password{isOld ? " (optional)" : ""}
              </label>
              <input
                id="af-pw"
                type="password"
                value={f.password}
                onChange={onPasswordChange}
                autoComplete="new-password"
                placeholder={isEdit && account && account.hasPassword ? "stored — type to replace" : ""}
              />
              <p className="dev-help">
                The INVESTOR (read-only) password, never the master one: a read-only login is what
                makes every trade and withdrawal call fail at the broker. Stored encrypted and never
                sent back to this page, which is why the field is blank.
                {isEdit && account && account.hasPassword && " Leave it blank to keep the stored one."}
                {isOld && " Leave it blank if the credentials are gone — the account still saves."}
            </p>
          </div>

          {/* ------------------------- 2. the connection test ------------------- */}
          <div className="dev-field">
              <label>
                Connection{" "}
                <span className="dev-step">{isOld ? "optional" : "step 2"}</span>
              </label>
              <div className="dev-inline-add">
                <button
                  type="button"
                  className="dev-btn"
                  onClick={runTest}
                  /* Needs something to test WITH. On an edit that can be the stored
                     password; anywhere else one has to be typed, and offering a
                     button that can only fail is worse than disabling it. */
                  disabled={
                    test.status === "testing" ||
                    !f.host.trim() ||
                    !String(f.login).trim() ||
                    (!f.password && !(isEdit && account && account.hasPassword))
                  }
                >
                  {test.status === "testing" ? (
                    <>
                      <Loader size={13} className="dev-spin" aria-hidden="true" /> <span>Testing…</span>
                    </>
                  ) : (
                    <>
                      <Plug size={13} aria-hidden="true" /> <span>Test connection</span>
                    </>
                  )}
                </button>
                {test.status === "ok" && (
                  <span className="dev-flag dev-flag-on">
                    <CircleCheck size={12} aria-hidden="true" />
                    {test.company || "connected"}
                    {test.stale ? " (last test)" : ""}
                  </span>
                )}
                {test.status === "fail" && (
                  <span className="dev-flag dev-flag-bad">
                    <X size={12} aria-hidden="true" /> failed
                  </span>
                )}
              </div>
              {test.status === "fail" && <p className="dev-help dev-help-bad">{test.error}</p>}
              {test.warning && (
                <p className="dev-warn">
                  <TriangleAlert size={15} aria-hidden="true" />
                  <span>{test.warning}</span>
                </p>
              )}
              {test.status === "ok" && test.company && (
                <p className="dev-help">
                  The broker named itself <strong>{test.company}</strong>, and that is the broker this
                  account is filed under. It is what the bot-IP check below is scoped to.
                </p>
            )}
          </div>

          {/* ------------------------------ the broker -------------------------- */}
          <div className="dev-field">
            <label htmlFor="af-broker">Broker</label>
            {/* Locked once a test has named the broker, in EITHER mode: the server's
                own name for itself beats anything chosen by hand, and leaving it
                editable would invite disagreeing with the evidence. */}
            <select id="af-broker" value={f.brokerId} onChange={set("brokerId")} disabled={test.status === "ok"}>
              <option value="">
                {test.status === "ok" ? "— from the connection test —" : isOld ? "— choose —" : "— from the connection test —"}
              </option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {isOld && test.status !== "ok" && (
              <div className="dev-inline-add">
                <input
                  type="text"
                  value={newBroker}
                  onChange={(e) => setNewBroker(e.target.value)}
                  placeholder="or add a broker by name"
                />
                <button type="button" className="dev-btn dev-btn-sm" onClick={addBroker} disabled={!newBroker.trim()}>
                  Add
                </button>
              </div>
            )}
          </div>

          {/* ------------------------------ 3. the bot server -------------------
              ONE DROPDOWN, because there is one thing to choose.
              The IP and the label are two facts about a single machine, kept
              together in one record (models/BotServerSchema.js, unique on the
              IP). Offering them as two selects meant two controls that always
              held the same value and always moved together - the second could
              only ever restate the first, and a reader would reasonably expect
              two controls to mean two decisions. They are two INPUTS only while
              CREATING a machine, below, which is the one moment the two facts
              are not yet joined.
              `selectedServerId` is derived from the saved IP rather than held
              separately, so an account whose machine predates this form still
              shows as selected. */}
          <div className="dev-row">
            <div className="dev-field">
              <label htmlFor="af-ip">
                Bot server <span className="dev-step">step 3</span>
              </label>
              <select id="af-ip" value={selectedServerId} onChange={(e) => pickServer(e.target.value)}>
                <option value="">— none —</option>
                {servers.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.ip}
                    {sv.name ? ` — ${sv.name}` : " — (no label)"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* AN IP RECORDED BEFORE THIS FORM, or a machine since deleted, would
              otherwise vanish from a dropdown that cannot show it - and the field
              would read "none" while the account still has one. Said plainly
              instead. */}
          {f.botIp.trim() && !selectedServerId && (
            <p className="dev-hint">
              This account is on <strong>{f.botIp.trim()}</strong>
              {f.botServerName.trim() ? ` (${f.botServerName.trim()})` : ""}, which is not in the list
              above. Add it below to pick it, or choose another machine.
            </p>
          )}

          {addingServer ? (
            <div className="dev-row">
              <div className="dev-field">
                <label htmlFor="af-newip">New server IP</label>
                <input
                  id="af-newip"
                  type="text"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  placeholder="10.0.0.1"
                />
              </div>
              <div className="dev-field">
                <label htmlFor="af-newipname">Its label</label>
                <div className="dev-inline-add">
                  <input
                    id="af-newipname"
                    type="text"
                    value={newIpName}
                    onChange={(e) => setNewIpName(e.target.value)}
                    placeholder="VPS-1"
                  />
                  <button
                    type="button"
                    className="dev-btn dev-btn-sm"
                    onClick={addServer}
                    disabled={!newIp.trim()}
                  >
                    Add
                  </button>
                  {servers.length > 0 && (
                    <button
                      type="button"
                      className="dev-ghost-btn dev-ghost-btn-sm"
                      onClick={() => {
                        setAddingServer(false);
                        setNewIp("");
                        setNewIpName("");
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="dev-hint">
              <button
                type="button"
                className="dev-ghost-btn dev-ghost-btn-sm"
                onClick={() => setAddingServer(true)}
              >
                + Add a new server
              </button>{" "}
              The label belongs to the machine, so every account on this IP shows the same one.
            </p>
          )}

          {!f.userId && f.botIp.trim() && (
            <p className="dev-hint">Choose a username first — the IP rule compares users.</p>
          )}
          {ipPending && (
            <p className="dev-hint">
              The IP will be checked as soon as the connection test names the broker — the rule is
              scoped to a broker, and checking against the wrong one would answer wrongly. It looks
              across <strong>every container</strong>, because the broker sees one IP and does not
              know what a container is.
            </p>
          )}
          {ipChecking && <p className="dev-hint">Checking that IP…</p>}

          {/* Info cases: shown inline, never blocking. */}
          {ip && ip.level === "info" && (
            <div className="dev-info">
              <Info size={15} aria-hidden="true" />
              <div>
                <strong>
                  {ip.ip}
                  {ip.serverName ? ` (${ip.serverName})` : ""} is already in use.
                </strong>{" "}
                Not a problem — listed so you know.
                <ul>
                  {/* THE CONTAINER IS NAMED because this check spans every one
                      of them — the broker sees one IP behaving as one or two
                      people and does not know what a container is. Without the
                      name, the list shows a login you would then go looking for
                      in the container you happen to have selected. */}
                  {ip.matches.map((m) => (
                    <li key={m.accountId}>
                      {m.login} · {m.broker} · {m.user || "(no user)"} ·{" "}
                      <em className="dev-ip-container">{m.container}</em>
                      {m.sameBroker ? " — same broker, same person" : " — another broker"}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {ip && ip.level === "clear" && f.botIp.trim() && !ipChecking && (
            <p className="dev-hint">That IP is not used by anyone at this broker.</p>
          )}

          {overrideIp && (
            <p className="dev-hint">
              Continuing with {f.botIp.trim()} despite the warning, as chosen.
            </p>
          )}

          <div className="dev-field">
            <label htmlFor="af-note">Note</label>
            <input id="af-note" type="text" value={f.note} onChange={set("note")} />
          </div>
        </div>

        <div className="dev-modal-foot">
          {blockers.length > 0 ? (
            <p className="dev-blockers">
              <TriangleAlert size={14} aria-hidden="true" />
              <span>Still needed: {blockers.join(", ")}</span>
            </p>
          ) : (
            <p className="dev-ready">
              <CircleCheck size={14} aria-hidden="true" />
              <span>Ready to save</span>
            </p>
          )}
          <div className="dev-modal-actions">
            <button type="button" className="dev-ghost-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="dev-btn" onClick={save} disabled={blockers.length > 0 || saving}>
              {saving ? "Saving…" : isEdit ? "Update" : "Add account"}
            </button>
          </div>
        </div>
      </div>

      {/* --------------------- the warning, as a modal over all -------------- */}
      {ip && ip.level === "warning" && !overrideIp && (
        <div className="dev-modal-back dev-modal-back-top" role="alertdialog" aria-modal="true">
          <div className="dev-modal dev-modal-narrow">
            <div className="dev-modal-head dev-modal-head-warn">
              <h3>
                <TriangleAlert size={16} aria-hidden="true" /> That IP is used by someone else at this
                broker
              </h3>
            </div>
            <div className="dev-modal-body">
              <p>
                <strong>{ip.ip}</strong>
                {ip.serverName ? ` (${ip.serverName})` : ""} already runs accounts belonging to a
                different person at this same broker:
              </p>
              <ul className="dev-list">
                {ip.sameBrokerDifferentUser.map((m) => (
                  <li key={m.accountId}>
                    {m.login} · {m.broker} · <strong>{m.user || "(no user assigned)"}</strong> ·{" "}
                    <em className="dev-ip-container">{m.container}</em>
                  </li>
                ))}
              </ul>
              {ip.otherBrokers.length > 0 && (
                <p className="dev-help">
                  It is also used at other brokers, which is not itself a concern:{" "}
                  {ip.otherBrokers.map((m) => `${m.login} (${m.broker}, ${m.container})`).join(", ")}.
                </p>
              )}
              <p className="dev-help">
                An account with no user assigned counts as a different person — it could belong to
                anyone, and treating unknown as safe is how a check like this stops firing.
              </p>
            </div>
            <div className="dev-modal-foot">
              <div className="dev-modal-actions">
                <button
                  type="button"
                  className="dev-ghost-btn"
                  onClick={() => {
                    setIp(null);
                    setF((prev) => ({ ...prev, botIp: "" }));
                    const el = document.getElementById("af-ip");
                    if (el) el.focus();
                  }}
                >
                  Change the bot IP
                </button>
                <button type="button" className="dev-btn" onClick={() => setOverrideIp(true)}>
                  Anyway continue with that IP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
